'use client'

import { useEffect } from 'react'

/**
 * Bridge between the Capacitor native shell and the web app.
 *
 * Pure side-effect component — renders nothing. Mount once in the
 * app-shell layout; on web (no Capacitor) the effect bails out early
 * but the `window.rezaroNative` API is still installed so calling
 * code can `haptic('selection')` from anywhere without branching.
 */

interface CapacitorGlobal {
  isNativePlatform?: () => boolean
  Plugins?: {
    PushNotifications?: {
      requestPermissions: () => Promise<{ receive: 'granted' | 'denied' | 'prompt' }>
      register: () => Promise<void>
      addListener: (event: string, cb: (data: unknown) => void) => void
    }
    StatusBar?: {
      setBackgroundColor: (opts: { color: string }) => Promise<void>
      setStyle: (opts: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }) => Promise<void>
    }
    SplashScreen?: { hide: () => Promise<void> }
    Haptics?: {
      impact: (opts: { style: 'HEAVY' | 'MEDIUM' | 'LIGHT' }) => Promise<void>
      selectionStart?: () => Promise<void>
      selectionChanged?: () => Promise<void>
      notification?: (opts: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }) => Promise<void>
    }
    App?: {
      addListener: (event: string, cb: (data: unknown) => void) => Promise<{ remove: () => void }>
    }
    Browser?: {
      open: (opts: { url: string }) => Promise<void>
      close: () => Promise<void>
    }
  }
}

/** Custom DOM event fired whenever the app returns to the foreground (native OR web tab refocused). */
export const REZARO_RESUME_EVENT = 'rezaro:resume'

type HapticIntent = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection'

interface RezaroNative {
  isNative: boolean
  platform: 'ios' | 'android' | 'web'
  haptic: (intent: HapticIntent) => void
}

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal
    rezaroNative?: RezaroNative
  }
}

/** Map a semantic intent to a vibration pattern (ms) for the Web Vibration API. */
const VIBRATION_PATTERN: Record<HapticIntent, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 35,
  success: [15, 50, 15],
  warning: [25, 60, 25],
  error: [40, 80, 40, 80, 40],
  selection: 8,
}

function installNativeApi() {
  if (typeof window === 'undefined') return
  if (window.rezaroNative) return

  const cap = window.Capacitor
  const isNative = !!cap?.isNativePlatform?.()
  const platform: RezaroNative['platform'] = isNative
    ? (/iPhone|iPad|iPod|Mac OS X/i.test(navigator.userAgent) ? 'ios' : 'android')
    : 'web'

  function haptic(intent: HapticIntent) {
    // Try the Capacitor Haptics plugin first (no-op if not installed).
    const h = cap?.Plugins?.Haptics
    if (h) {
      try {
        if (intent === 'selection') {
          h.selectionChanged?.()
        } else if (intent === 'success' || intent === 'warning' || intent === 'error') {
          h.notification?.({ type: intent.toUpperCase() as 'SUCCESS' | 'WARNING' | 'ERROR' })
        } else {
          h.impact({ style: intent === 'light' ? 'LIGHT' : intent === 'medium' ? 'MEDIUM' : 'HEAVY' })
        }
        return
      } catch { /* fall through to web vibration */ }
    }
    // Web Vibration API works in Android Chrome + Android WebView, no-op on iOS Safari.
    try { navigator.vibrate?.(VIBRATION_PATTERN[intent]) } catch { /* unsupported */ }
  }

  window.rezaroNative = { isNative, platform, haptic }
}

export function NativeBridge() {
  useEffect(() => {
    installNativeApi()

    // Foreground-resume signal — fires when the browser tab regains
    // focus OR when the native app comes back from background. Pages
    // can `addEventListener('rezaro:resume', refetch)` to refresh their
    // data so an operator who just got a push alert sees fresh state.
    function dispatchResume() {
      window.dispatchEvent(new CustomEvent(REZARO_RESUME_EVENT))
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') dispatchResume()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', dispatchResume)

    const cap = typeof window !== 'undefined' ? window.Capacitor : undefined
    if (!cap?.isNativePlatform?.()) {
      return () => {
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('focus', dispatchResume)
      }
    }

    const plugins = cap.Plugins ?? {}

    // Native App plugin: fires on Android resume + iOS willEnterForeground.
    let appResumeUnsub: (() => void) | undefined
    plugins.App?.addListener('appStateChange', (state: unknown) => {
      const s = state as { isActive?: boolean }
      if (s?.isActive) dispatchResume()
    }).then((handle) => { appResumeUnsub = handle.remove }).catch(() => {})

    // Deep-link OAuth return. Google blocks OAuth inside a WebView, so the
    // sign-in pages open the provider in a system Custom Tab with a
    // `rezaro://auth/callback` redirect. When auth completes the OS reopens
    // the app with that URL; we exchange the PKCE code *inside* the app's
    // WebView (the code_verifier lives here, not in the Custom Tab) so the
    // session lands in the app — not the external browser.
    let appUrlUnsub: (() => void) | undefined
    plugins.App?.addListener('appUrlOpen', async (data: unknown) => {
      const url = (data as { url?: string })?.url
      if (!url || !url.includes('auth/callback')) return
      try {
        const qIndex = url.indexOf('?')
        const params = new URLSearchParams(qIndex >= 0 ? url.slice(qIndex + 1) : '')
        const code = params.get('code')
        const nextParam = params.get('next')
        const { createClient } = await import('@/lib/supabase/client')
        const supabase = createClient()
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) { window.location.href = '/signin?error=auth_callback_failed'; return }
        }
        plugins.Browser?.close().catch(() => { /* tab already gone */ })
        if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) {
          window.location.href = nextParam
          return
        }
        // Mirror the web /auth/callback smart redirect: owner's restaurant
        // → day view, otherwise profile completion.
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) { window.location.href = '/signin?error=auth_callback_failed'; return }
        const { data: rest } = await supabase
          .from('restaurants').select('slug').eq('owner_id', user.id).maybeSingle()
        window.location.href = rest?.slug ? `/${rest.slug}/bookings/day` : '/onboarding/complete-profile'
      } catch {
        window.location.href = '/signin?error=auth_callback_failed'
      }
    }).then((handle) => { appUrlUnsub = handle.remove }).catch(() => {})

    // Hide splash as soon as the React tree is ready.
    plugins.SplashScreen?.hide().catch(() => { /* not loaded */ })

    // Match status bar to our dark brand surface.
    plugins.StatusBar?.setBackgroundColor({ color: '#0D0B17' }).catch(() => {})
    plugins.StatusBar?.setStyle({ style: 'DARK' }).catch(() => {})

    // Push registration — best-effort. The user can decline.
    const pn = plugins.PushNotifications
    if (pn) {
      ;(async () => {
        try {
          const perm = await pn.requestPermissions()
          if (perm.receive !== 'granted') return

          pn.addListener('registration', (token) => {
            const value = (token as { value?: string })?.value
            if (!value) return
            const platform = /iPhone|iPad|iPod|Mac OS X/i.test(navigator.userAgent) ? 'ios' : 'android'
            fetch('/api/push/register', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: value, platform }),
            }).catch(() => { /* will retry on next launch */ })
          })

          // Tap a push notification → navigate to the URL the server attached.
          pn.addListener('pushNotificationActionPerformed', (event) => {
            const e = event as { notification?: { data?: Record<string, string> } }
            const url = e.notification?.data?.url
            if (url && typeof url === 'string') {
              // Use full nav so the wrapped webview loads the booking detail
              // even if React's client-side router missed the deep-link.
              window.location.href = url
            }
          })

          await pn.register()
        } catch { /* native plugin not available */ }
      })()
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', dispatchResume)
      appResumeUnsub?.()
      appUrlUnsub?.()
    }
  }, [])

  return null
}

/**
 * Convenience hook for any client component.
 *
 *   const { haptic, isNative } = useNative()
 *   <button onClick={() => { haptic('selection'); ... }} />
 */
export function useNative(): RezaroNative {
  if (typeof window === 'undefined') {
    return { isNative: false, platform: 'web', haptic: () => {} }
  }
  if (!window.rezaroNative) installNativeApi()
  return window.rezaroNative ?? { isNative: false, platform: 'web', haptic: () => {} }
}
